<template>
  <div v-if="assisting" class="assist-banner" role="status" data-testid="assist-banner">
    <span class="assist-banner__icon material-icons" aria-hidden="true">support_agent</span>
    <span class="assist-banner__text">
      You are working as <b data-testid="assist-subject">{{ assisting.subjectName ?? assisting.subjectPassportId }}</b
      >. Everything you do is recorded as you.
      <small class="assist-banner__reason" data-testid="assist-reason">{{ assisting.reason }}</small>
    </span>
    <span class="assist-banner__left" data-testid="assist-remaining">{{ remaining }}</span>
    <button type="button" class="assist-banner__stop" data-testid="assist-stop" @click="emit('stop')">Stop assisting</button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, type PropType } from "vue";
import type { HostAssisting } from "@/state/globalState";

/**
 * 🔴 The banner an operator cannot miss (D34).
 *
 * An operator who FORGETS they are assisting is the way this feature turns into an incident: a note
 * typed as somebody else, a decision taken in their name, an action nobody can explain afterwards.
 * So this is not a subtle tint on the top bar — it is a bar of its own, above everything, for the
 * whole session, saying who you are working as and how long is left.
 *
 * It also carries the REASON the operator gave. Support with no reason recorded is surveillance, and
 * a reason recorded where nobody reads it is barely better — so it is on the screen, in front of the
 * person who wrote it.
 */
export default defineComponent({
  name: "AssistBanner",
  props: {
    assisting: { type: Object as PropType<HostAssisting | null>, default: null },
  },
  emits: ["stop"],
  setup(props, { emit }) {
    const now = ref(Date.now());
    let tick: ReturnType<typeof setInterval> | null = null;

    onMounted(() => {
      tick = setInterval(() => (now.value = Date.now()), 1000);
    });
    onBeforeUnmount(() => {
      if (tick !== null) clearInterval(tick);
      tick = null;
    });

    /**
     * Minutes and seconds, counting down. Not an end TIME: "14:58 left" is a fact an operator acts on,
     * where "ends at 09:41" is arithmetic they have to do while somebody is on the phone.
     */
    const remaining = computed(() => {
      if (props.assisting === null) return "";
      const left = Math.max(0, props.assisting.until - now.value);
      const minutes = Math.floor(left / 60_000);
      const seconds = Math.floor((left % 60_000) / 1000);
      return left === 0 ? "ended" : `${minutes}:${String(seconds).padStart(2, "0")} left`;
    });

    return { remaining, emit };
  },
});
</script>
